import React, { useState, useEffect } from "react";

class PanelErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError)
      return <div data-testid={this.props.errorTestId} style={{ padding: "8px 16px", color: "var(--t3)", fontSize: 12 }}>Panel error.</div>;
    return this.props.children;
  }
}

// Health sub-panel components — extracted so PanelErrorBoundary can catch their render crashes.
function SilentlyStuckSection({ data }) {
  return (
    <div
      data-testid="ss-section"
      data-alarm={data.length > 0 ? "true" : "false"}
      style={{ borderBottom: "1px solid var(--border)", background: data.length > 0 ? "rgba(224,80,80,0.05)" : undefined }}
    >
      <div style={{ padding: "8px 16px 4px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: data.length > 0 ? "var(--red)" : "var(--t3)" }}>
        Silently Stuck{data.length > 0 ? ` (${data.length})` : ""}
      </div>
      {data.length === 0
        ? <div style={obsMuted}>Nothing stuck.</div>
        : (
          <table style={{ ...obsTblStyle, marginBottom: 8 }}>
            <thead><tr>{["Batch", "Angle", "Unscored", "Newest Unscored At", "auto_score", "State"].map(c => <th key={c} style={obsTh}>{c}</th>)}</tr></thead>
            <tbody>
              {data.map(r => (
                <tr key={r.batch_id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ ...obsTd, color: "var(--red)", fontWeight: 600 }}>{r.batch_id}</td>
                  <td style={obsTd}>{r.angle ?? "—"}</td>
                  <td style={{ ...obsTd, color: "var(--red)" }}>{r.unscored_count}</td>
                  <td style={obsTd}>{r.newest_unscored_at}</td>
                  <td style={obsTd}>{r.auto_score}</td>
                  <td style={obsTd}>{r.scoring_state}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </div>
  );
}

function StuckRunningSection({ data }) {
  return (
    <div
      data-testid="sr-section"
      data-alarm={data.length > 0 ? "true" : "false"}
      style={{ borderBottom: "1px solid var(--border)", background: data.length > 0 ? "rgba(224,80,80,0.05)" : undefined }}
    >
      <div style={{ padding: "8px 16px 4px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: data.length > 0 ? "var(--red)" : "var(--t3)" }}>
        Running (scoring_state=running){data.length > 0 ? ` (${data.length})` : ""}
      </div>
      {data.length === 0
        ? <div style={obsMuted}>None running.</div>
        : (
          <table style={{ ...obsTblStyle, marginBottom: 8 }}>
            <thead><tr>{["Batch", "Angle", "State", "Rescue Count", "Minutes Since Start"].map(c => <th key={c} style={obsTh}>{c}</th>)}</tr></thead>
            <tbody>
              {data.map(r => (
                <tr key={r.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ ...obsTd, color: "var(--red)", fontWeight: 600 }}>{r.id}</td>
                  <td style={obsTd}>{r.angle ?? "—"}</td>
                  <td style={obsTd}>{r.scoring_state}</td>
                  <td style={obsTd}>{r.rescue_count}</td>
                  <td style={{ ...obsTd, color: "var(--red)" }}>{r.minutes_since_started ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </div>
  );
}

function RescueEventsSection({ summary }) {
  return (
    <div style={{ borderBottom: "1px solid var(--border)" }}>
      <div style={{ padding: "8px 16px 4px", fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.07em" }}>
        Rescue Events
      </div>
      {!summary
        ? <div data-testid="rescue-panel-error" style={obsMuted}>Data unavailable.</div>
        : summary.total === 0
          ? <div style={obsMuted}>No rescues.</div>
          : (
            <div style={{ padding: "8px 16px 12px", fontSize: 12 }}>
              <div style={{ color: "var(--t2)" }}>Total: {summary.total}</div>
              {summary.last_event && (
                <div style={{ color: "var(--t3)", marginTop: 4 }}>
                  Last: {summary.last_event.recorded_at}
                  {summary.last_event.path ? ` via ${summary.last_event.path}` : ""}
                  {summary.last_event.batch_id != null ? ` (batch ${summary.last_event.batch_id})` : ""}
                </div>
              )}
              {summary.affected_batches.length > 0 && (
                <div style={{ color: "var(--t3)", marginTop: 4 }}>
                  Affected batches: {summary.affected_batches.join(", ")}
                </div>
              )}
            </div>
          )
      }
    </div>
  );
}

function UnscorableBacklogSection({ data }) {
  return (
    <div style={{ borderBottom: "1px solid var(--border)" }}>
      <div style={{ padding: "8px 16px 4px", fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.07em" }}>
        Unscorable Backlog{" "}
        <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(non-scorable angles — informational)</span>
      </div>
      {data.length === 0
        ? <div style={obsMuted}>Empty.</div>
        : (
          <table style={{ ...obsTblStyle, marginBottom: 8 }}>
            <thead><tr>{["Batch", "Angle", "Unscored", "Newest Unscored At", "auto_score"].map(c => <th key={c} style={obsTh}>{c}</th>)}</tr></thead>
            <tbody>
              {data.map(r => (
                <tr key={r.batch_id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={obsTd}>{r.batch_id}</td>
                  <td style={obsTd}>{r.angle ?? "—"}</td>
                  <td style={obsTd}>{r.unscored_count}</td>
                  <td style={obsTd}>{r.newest_unscored_at}</td>
                  <td style={obsTd}>{r.auto_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </div>
  );
}

function ScoreErrorsSection({ data }) {
  return (
    <div style={{ borderBottom: "1px solid var(--border)" }}>
      <div style={{ padding: "8px 16px 4px", fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.07em" }}>
        Score Errors by Batch
      </div>
      {data.length === 0
        ? <div style={obsMuted}>None.</div>
        : (
          <table style={{ ...obsTblStyle, marginBottom: 8 }}>
            <thead><tr>{["Batch", "Error Count"].map(c => <th key={c} style={obsTh}>{c}</th>)}</tr></thead>
            <tbody>
              {data.map(r => (
                <tr key={r.batch_id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ ...obsTd, color: "var(--amber)", fontWeight: 600 }}>{r.batch_id}</td>
                  <td style={{ ...obsTd, color: "var(--red)" }}>{r.error_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </div>
  );
}

function ConsistencyLogSection({ data }) {
  return (
    <div>
      <div style={{ padding: "8px 16px 4px", fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.07em" }}>
        Consistency Log (last 10)
      </div>
      {data.length === 0
        ? <div style={obsMuted}>Empty.</div>
        : (
          <table style={{ ...obsTblStyle, marginBottom: 8 }}>
            <thead><tr>{["ID", "Recorded At", "Corrected", "Unparseable", "First Error"].map(c => <th key={c} style={obsTh}>{c}</th>)}</tr></thead>
            <tbody>
              {data.map(r => (
                <tr key={r.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={obsTd}>{r.id}</td>
                  <td style={obsTd}>{r.recorded_at}</td>
                  <td style={obsTd}>{r.corrected}</td>
                  <td style={obsTd}>{r.unparseable}</td>
                  <td style={{ ...obsTd, fontFamily: "monospace", fontSize: 11 }}>{r.first_error ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </div>
  );
}

function LeadAuditContent({ leadData }) {
  const { lead, score_history } = leadData;
  return (
    <div style={{ borderTop: "1px solid var(--border)" }}>
      <div style={{ padding: "10px 16px 4px", fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.07em" }}>
        Lead {lead.id} &mdash; {lead.name} &middot; {lead.title ?? "—"} &middot; score: {lead.score ?? "—"}
      </div>
      {lead.score_error && (
        <div style={{ padding: "4px 16px 8px", fontSize: 12, color: "var(--red)", fontWeight: 600 }}>
          Score error: {lead.score_error}
        </div>
      )}
      <table style={{ ...obsTblStyle, marginBottom: 12 }}>
        <thead><tr>
          {["#", "Event", "Prompt Version", "Model", "Score", "Scored At", "Recorded At"].map(c => <th key={c} style={obsTh}>{c}</th>)}
        </tr></thead>
        <tbody>
          {score_history.map((ev, i) => (
            <tr key={ev.id} style={{ borderBottom: "1px solid var(--border)" }}>
              <td style={{ ...obsTd, color: "var(--t3)" }}>{i + 1}</td>
              <td style={{ ...obsTd, fontWeight: 600 }}>{ev.event_type}</td>
              <td style={{ ...obsTd, fontFamily: "monospace", fontSize: 11 }}>{ev.prompt_version ?? "—"}</td>
              <td style={{ ...obsTd, fontFamily: "monospace", fontSize: 11 }}>{ev.model ?? "—"}</td>
              <td style={{ ...obsTd, color: ev.score != null && ev.score >= 80 ? "var(--amber)" : undefined }}>{ev.score ?? "—"}</td>
              <td style={obsTd}>{ev.scored_at ?? "—"}</td>
              <td style={obsTd}>{ev.recorded_at ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {lead.score_meta_parsed && (
        <div style={{ padding: "0 16px 12px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>score_meta</div>
          <pre style={{ fontSize: 11, color: "var(--t2)", background: "var(--bg3)", borderRadius: 6, padding: "10px 12px", overflow: "auto", maxHeight: 300, whiteSpace: "pre-wrap", margin: 0 }}>
            {JSON.stringify(lead.score_meta_parsed, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function BatchInspectorContent({ batchData }) {
  const { pipeline, scoring, plan } = batchData;
  const bkts = scoring.buckets;
  return (
    <div>
      <div style={{ borderTop: "1px solid var(--border)", padding: "10px 16px 4px" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
          Pipeline — batch {pipeline.batch_id} &middot; {pipeline.run_count} run{pipeline.run_count !== 1 ? "s" : ""}
          {pipeline.parallelism_factor != null && (
            <span data-testid="parallelism-factor" style={{ marginLeft: 12, color: "var(--amber)", fontWeight: 700 }}>
              parallelism {pipeline.parallelism_factor}&times;
            </span>
          )}
          {" "}· sum vendor {pipeline.sum_vendor_duration_seconds}s
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={obsTblStyle}>
            <thead><tr>
              {["ID", "Status", "Created", "Vend Start", "Vend Finish", "Completed", "Dur (s)", "Req Items", "Slice Start", "Slice Max", "Staged", "Dupes", "Total Elems"].map(c => <th key={c} style={obsTh}>{c}</th>)}
            </tr></thead>
            <tbody>
              {pipeline.runs.map(r => (
                <tr key={r.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={obsTd}>{r.id}</td>
                  <td style={obsTd}>{r.status}</td>
                  <td style={{ ...obsTd, whiteSpace: "nowrap" }}>{r.created_at ? r.created_at.slice(0, 19).replace("T", " ") : "—"}</td>
                  <td style={{ ...obsTd, whiteSpace: "nowrap" }}>{r.vendor_started_at ? r.vendor_started_at.slice(0, 19).replace("T", " ") : "—"}</td>
                  <td style={{ ...obsTd, whiteSpace: "nowrap" }}>{r.vendor_finished_at ? r.vendor_finished_at.slice(0, 19).replace("T", " ") : "—"}</td>
                  <td style={{ ...obsTd, whiteSpace: "nowrap" }}>{r.completed_at ? r.completed_at.slice(0, 19).replace("T", " ") : "—"}</td>
                  <td style={obsTd}>{r.vendor_duration_seconds ?? "—"}</td>
                  <td style={obsTd}>{r.requested_items ?? "—"}</td>
                  <td style={obsTd}>{r.slice_start_page ?? "—"}</td>
                  <td style={obsTd}>{r.slice_max_items ?? "—"}</td>
                  <td style={obsTd}>{r.staged ?? "—"}</td>
                  <td style={obsTd}>{r.intra_batch_dupes ?? "—"}</td>
                  <td style={obsTd}>{r.total_elements ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ borderTop: "1px solid var(--border)", padding: "10px 16px 4px" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
          Scoring — state: {scoring.batch.scoring_state} &middot; rescues: {scoring.batch.rescue_count}
        </div>
        <table style={{ ...obsTblStyle, marginBottom: 10 }}>
          <thead><tr>
            {["80+", "60–79", "40–59", "lt40", "zero", "unscored", "errored", "total"].map(c => <th key={c} style={obsTh}>{c}</th>)}
          </tr></thead>
          <tbody>
            <tr>
              <td style={obsTd}>{bkts._80plus}</td>
              <td style={obsTd}>{bkts._60_79}</td>
              <td style={obsTd}>{bkts._40_59}</td>
              <td style={obsTd}>{bkts.lt40}</td>
              <td style={obsTd}>{bkts.zero}</td>
              <td style={obsTd}>{bkts.unscored}</td>
              <td style={obsTd}>{bkts.errored}</td>
              <td style={{ ...obsTd, fontWeight: 600 }}>{bkts.total}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: "var(--t2)" }}>
            <span style={{ color: "var(--t3)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>guard retries: </span>
            {scoring.guard_retry_count}
          </div>
          <div data-testid="double-scored-field" style={{ fontSize: 12, color: "var(--t3)" }}>
            <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>score events &gt;1 (all generations — includes legitimate rescores until OBS-1d): </span>
            {scoring.double_scored.count}
          </div>
        </div>
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Prompt Version Census</div>
          <table style={obsTblStyle}>
            <thead><tr><th style={obsTh}>Version</th><th style={obsTh}>Count</th></tr></thead>
            <tbody>
              {Object.entries(scoring.prompt_version_census).map(([v, n]) => (
                <tr key={v} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ ...obsTd, fontFamily: "monospace", fontSize: 11 }}>{v}</td>
                  <td style={obsTd}>{n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {Object.keys(scoring.rationale_flag_counts).length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Rationale Flags</div>
            <table style={obsTblStyle}>
              <thead><tr><th style={obsTh}>Flag</th><th style={obsTh}>Count</th></tr></thead>
              <tbody>
                {Object.entries(scoring.rationale_flag_counts).map(([flag, n]) => (
                  <tr key={flag} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={obsTd}>{flag}</td>
                    <td style={obsTd}>{n}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div style={{ borderTop: "1px solid var(--border)", padding: "10px 16px 12px" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>Plan</div>
        {!plan.linked
          ? <div style={{ fontSize: 12, color: "var(--t3)" }}>No plan linked (persistence ships in OBS-0d).</div>
          : (
            <div>
              <div style={{ fontSize: 12, color: "var(--t2)", marginBottom: 4 }}>
                Plan ID: {plan.plan.id} &middot; Model: {plan.plan.model} &middot; Created: {plan.plan.created_at}
              </div>
              {plan.plan.plan_json && (
                <pre style={{ fontSize: 11, color: "var(--t2)", background: "var(--bg3)", borderRadius: 6, padding: "10px 12px", overflow: "auto", maxHeight: 300, whiteSpace: "pre-wrap", margin: 0 }}>
                  {JSON.stringify(plan.plan.plan_json, null, 2)}
                </pre>
              )}
            </div>
          )
        }
      </div>
    </div>
  );
}

const API = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace(/\/api$/, '');

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("expertnet_token")}`,
  };
}

const ROLE_CHIP = {
  admin:     { color: "var(--amber)",  bg: "var(--amber-bg)",  border: "var(--amber-bdr)" },
  manager:   { color: "var(--teal)",   bg: "rgba(90,180,204,0.10)", border: "rgba(90,180,204,0.3)" },
  associate: { color: "var(--t3)",     bg: "rgba(90,122,144,0.10)", border: "rgba(90,122,144,0.25)" },
};

function RoleChip({ role }) {
  const s = ROLE_CHIP[role] || ROLE_CHIP.associate;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, color: s.color,
      background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: 5, padding: "2px 8px", textTransform: "capitalize",
    }}>{role}</span>
  );
}

function RoleToggle({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      {["associate", "manager"].map(r => (
        <button
          key={r}
          type="button"
          onClick={() => onChange(r)}
          style={{
            padding: "6px 16px", borderRadius: 7, fontSize: 12, fontWeight: 600,
            cursor: "pointer", textTransform: "capitalize",
            background: value === r ? "var(--amber-bg)" : "transparent",
            border: `1px solid ${value === r ? "var(--amber-bdr)" : "var(--border2)"}`,
            color: value === r ? "var(--amber)" : "var(--t3)",
          }}
        >{r}</button>
      ))}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "9px 12px", background: "var(--bg3)",
  border: "1px solid var(--border2)", borderRadius: 8,
  color: "var(--t1)", fontFamily: "'Inter', sans-serif", fontSize: 13,
  outline: "none", boxSizing: "border-box",
};

const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 700, color: "var(--t2)",
  textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6,
};

const panelStyle = {
  background: "var(--bg2)", border: "1px solid var(--border2)",
  borderRadius: 12, padding: "20px 20px 16px",
  display: "flex", flexDirection: "column", gap: 14, marginBottom: 20,
};

// OBS-2: Observability section markup — duplicated from SCHED-2 collapsible pattern.
// SchedulingView.js is untouched; this is an intentional inline duplication per OBS-2 amendment.
// SchedulingView uses var(--rasp-*); AdminDashboard uses var(--*) theme vars.
const obsSectionWrap = {
  background: "var(--bg2)", borderRadius: 10,
  border: "1px solid var(--border)", overflow: "hidden", marginBottom: 16,
};
const obsSectionHead = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "12px 16px", cursor: "pointer", userSelect: "none",
};
const obsTblStyle = { width: "100%", borderCollapse: "collapse", fontSize: 12 };
const obsTh = {
  fontSize: 10, fontWeight: 700, color: "var(--t3)",
  textTransform: "uppercase", letterSpacing: "0.06em",
  padding: "8px 12px", borderBottom: "1px solid var(--border)", textAlign: "left",
};
const obsTd = { padding: "8px 12px", color: "var(--t2)", verticalAlign: "top" };
const obsMuted = { fontSize: 12, color: "var(--t3)", padding: "8px 16px 12px" };

function ObsChevron({ collapsed }) {
  return (
    <span style={{
      color: "var(--t3)", fontSize: 14, display: "inline-block",
      transform: collapsed ? "rotate(-90deg)" : "none", transition: "transform 0.15s",
    }}>&#9662;</span>
  );
}

function ObsErrorState({ error, onRetry }) {
  if (error === "403")
    return <div data-testid="obs-forbidden" style={{ padding: "16px", color: "var(--t3)", fontSize: 13 }}>Admin access required.</div>;
  if (error === "404")
    return <div style={{ padding: "16px", color: "var(--t3)", fontSize: 13 }}>Not found.</div>;
  return (
    <div style={{ padding: "16px", display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ fontSize: 13, color: "var(--t3)" }}>Network error.</span>
      {onRetry && (
        <button onClick={onRetry} style={{
          padding: "4px 12px", background: "transparent",
          border: "1px solid var(--border2)", borderRadius: 6,
          color: "var(--t2)", fontSize: 11, cursor: "pointer",
        }}>Retry</button>
      )}
    </div>
  );
}

export default function AdminDashboard({ currentUser, onLogout }) {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", role: "associate" });
  const [createdInvite, setCreatedInvite] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ username: "", role: "associate" });
  const [pendingInvite, setPendingInvite] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [overviewError, setOverviewError] = useState(null);
  const [teamsData, setTeamsData] = useState([]);
  const [teamsError, setTeamsError] = useState(null);
  const [expandedManagers, setExpandedManagers] = useState({});
  const [expandedAssociates, setExpandedAssociates] = useState({});
  const [globalSettings, setGlobalSettings] = useState({});
  const [settingsSaved, setSettingsSaved] = useState(false);

  // OBS-2 state
  const [obsCollapsed, setObsCollapsed] = useState({ health: false, inspector: true, audit: true });
  const [obsHealth, setObsHealth] = useState(null);
  const [obsHealthLoading, setObsHealthLoading] = useState(false);
  const [obsHealthError, setObsHealthError] = useState(null);
  const [obsBatchId, setObsBatchId] = useState('');
  const [obsBatchData, setObsBatchData] = useState(null);
  const [obsBatchLoading, setObsBatchLoading] = useState(false);
  const [obsBatchError, setObsBatchError] = useState(null);
  const [obsLeadId, setObsLeadId] = useState('');
  const [obsLeadData, setObsLeadData] = useState(null);
  const [obsLeadLoading, setObsLeadLoading] = useState(false);
  const [obsLeadError, setObsLeadError] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [mailboxesError, setMailboxesError] = useState(null);
  const [mailboxesInlineErrors, setMailboxesInlineErrors] = useState({});
  const [grantTarget, setGrantTarget] = useState(null);
  const [grantAmount, setGrantAmount] = useState('');
  const [grantError, setGrantError] = useState(null);
  const [adminCredits, setAdminCredits] = useState(null);
  const [adminCreditsError, setAdminCreditsError] = useState(null);

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    if (tab !== "overview") return;
    setOverviewError(null);
    fetch(`${API}/api/admin/overview`, { headers: authHeaders() })
      .then(r => { if (r.ok) return r.json(); throw new Error('error'); })
      .then(d => setOverviewData(d))
      .catch(() => setOverviewError('error'));
  }, [tab]);

  useEffect(() => {
    if (tab !== "teams") return;
    fetchTeams();
  }, [tab]);

  useEffect(() => {
    if (tab !== "settings") return;
    fetchGlobalSettings();
  }, [tab]);

  useEffect(() => {
    if (tab !== "observability") return;
    loadObsHealth();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab !== "mailboxes") return;
    fetchMailboxes();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab !== "credits") return;
    setAdminCreditsError(null);
    fetch(`${API}/api/admin/credits`, { headers: authHeaders() })
      .then(r => { if (r.ok) return r.json(); throw new Error('error'); })
      .then(d => setAdminCredits(d))
      .catch(() => setAdminCreditsError('error'));
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/api/users`, { headers: authHeaders() });
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleGrantCredits = async (userId) => {
    setGrantError(null);
    const amt = parseInt(grantAmount, 10);
    if (isNaN(amt) || amt === 0) { setGrantError('Enter a nonzero integer'); return; }
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/credits`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ credits: amt }),
      });
      if (!res.ok) {
        const d = await res.json();
        setGrantError(d.error || `HTTP ${res.status}`);
        return;
      }
    } catch (e) { setGrantError(e.message); return; }
    setGrantTarget(null);
    setGrantAmount('');
    fetchUsers();
  };

  const fetchMailboxes = async () => {
    setMailboxesError(null);
    try {
      const res = await fetch(`${API}/api/admin/mailboxes`, { headers: authHeaders() });
      if (res.ok) setMailboxes(await res.json());
      else setMailboxesError('Failed to load mailboxes');
    } catch (e) { setMailboxesError('Failed to load mailboxes'); }
  };

  const assignMailbox = async (email, userId) => {
    setMailboxesInlineErrors(prev => ({ ...prev, [email]: null }));
    try {
      const res = await fetch(`${API}/api/admin/mailboxes/assign`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, user_id: userId }),
      });
      if (res.ok) {
        fetchMailboxes();
      } else {
        const d = await res.json().catch(() => ({}));
        setMailboxesInlineErrors(prev => ({ ...prev, [email]: d.error || 'Error' }));
      }
    } catch (e) {
      setMailboxesInlineErrors(prev => ({ ...prev, [email]: 'Network error' }));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/users`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ ...newUser, company_id: currentUser.company_id }),
      });
      if (res.ok) {
        const data = await res.json();
        setNewUser({ username: "", role: "associate" });
        setShowCreateForm(false);
        fetchUsers();
        if (data.token) {
          setCreatedInvite({ username: data.username, token: data.token, expires_at: data.expires_at });
        }
      }
    } catch (e) { console.error(e); }
  };

  const deactivateUser = async (user) => {
    if (!window.confirm(`Deactivate ${user.username}? They will be signed out and unable to log in.`)) return;
    try {
      await fetch(`${API}/api/users/${user.id}`, { method: "DELETE", headers: authHeaders() });
      fetchUsers();
    } catch (e) { console.error(e); }
  };

  const reactivateUser = async (user) => {
    try {
      await fetch(`${API}/api/users/${user.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ is_active: true }),
      });
      fetchUsers();
    } catch (e) { console.error(e); }
  };

  const issuePasswordLink = async (user) => {
    try {
      const res = await fetch(`${API}/api/users/${user.id}/password-link`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setPendingInvite({ username: user.username, token: data.token, expires_at: data.expires_at });
      }
    } catch (e) { console.error(e); }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setEditForm({ username: user.username, role: user.role });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const body = { username: editForm.username, role: editForm.role };
    try {
      const res = await fetch(`${API}/api/users/${editingUser.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (res.ok) { setEditingUser(null); fetchUsers(); }
    } catch (e) { console.error(e); }
  };

  const inviteLink = (token) => `${window.location.origin}/?invite=${token}`;

  const fetchTeams = async () => {
    setTeamsError(null);
    try {
      const res = await fetch(`${API}/api/admin/teams`, { headers: authHeaders() });
      if (res.ok) setTeamsData(await res.json());
      else setTeamsError('error');
    } catch (e) { setTeamsError('error'); }
  };

  const toggleManager = (id) => setExpandedManagers(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleAssociate = (id) => setExpandedAssociates(prev => ({ ...prev, [id]: !prev[id] }));

  const fetchGlobalSettings = async () => {
    const res = await fetch(`${API}/api/admin/settings`, { headers: authHeaders() });
    if (res.ok) setGlobalSettings(await res.json());
  };

  const saveGlobalSettings = async () => {
    const res = await fetch(`${API}/api/admin/settings`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(globalSettings),
    });
    if (res.ok) { setSettingsSaved(true); setTimeout(() => setSettingsSaved(false), 2000); }
  };

  // OBS-2 handlers
  const loadObsHealth = async () => {
    setObsHealthLoading(true);
    setObsHealthError(null);
    try {
      const res = await fetch(`${API}/api/admin/obs/system/health`, { headers: authHeaders() });
      if (res.status === 403) { setObsHealthError('403'); return; }
      if (!res.ok) { setObsHealthError('error'); return; }
      setObsHealth(await res.json());
    } catch (e) {
      setObsHealthError('network');
    } finally {
      setObsHealthLoading(false);
    }
  };

  const loadObsBatch = async () => {
    const id = parseInt(obsBatchId, 10);
    if (!id) return;
    setObsBatchLoading(true);
    setObsBatchError(null);
    setObsBatchData(null);
    try {
      const [pRes, sRes, plRes] = await Promise.all([
        fetch(`${API}/api/admin/obs/batch/${id}/pipeline`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/obs/batch/${id}/scoring`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/obs/batch/${id}/plan`, { headers: authHeaders() }),
      ]);
      if ([pRes, sRes, plRes].some(r => r.status === 403)) { setObsBatchError('403'); return; }
      if ([pRes, sRes, plRes].some(r => r.status === 404)) { setObsBatchError('404'); return; }
      if ([pRes, sRes, plRes].some(r => !r.ok)) { setObsBatchError('error'); return; }
      const [pipeline, scoring, plan] = await Promise.all([pRes.json(), sRes.json(), plRes.json()]);
      setObsBatchData({ pipeline, scoring, plan });
    } catch (e) {
      setObsBatchError('network');
    } finally {
      setObsBatchLoading(false);
    }
  };

  const loadObsLead = async () => {
    const id = parseInt(obsLeadId, 10);
    if (!id) return;
    setObsLeadLoading(true);
    setObsLeadError(null);
    setObsLeadData(null);
    try {
      const res = await fetch(`${API}/api/admin/obs/lead/${id}/audit`, { headers: authHeaders() });
      if (res.status === 403) { setObsLeadError('403'); return; }
      if (res.status === 404) { setObsLeadError('404'); return; }
      if (!res.ok) { setObsLeadError('error'); return; }
      setObsLeadData(await res.json());
    } catch (e) {
      setObsLeadError('network');
    } finally {
      setObsLeadLoading(false);
    }
  };

  const toggleObs = (key) => setObsCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div style={{ display: "flex", minHeight: "100vh", width: "100vw", background: "var(--bg)", fontFamily: "'Inter', sans-serif" }}>

      {/* Sidebar */}
      <div style={{
        width: 220, minWidth: 220, background: "rgba(5,12,20,0.95)",
        borderRight: "1px solid var(--border)", display: "flex",
        flexDirection: "column", padding: "14px 10px", minHeight: "100vh",
      }}>
        <div style={{ padding: "6px 10px 4px" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--amber)", letterSpacing: "-0.01em" }}>
            ExpertNet
          </div>
          <div style={{ fontSize: 10, fontWeight: 600, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 2 }}>
            Admin
          </div>
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ padding: "10px 10px 6px", borderTop: "1px solid var(--border)" }}>
          <div style={{ fontSize: 12, color: "var(--t2)", marginBottom: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {currentUser.username}
          </div>
          <button
            onClick={onLogout}
            style={{
              width: "100%", padding: "7px 0", background: "transparent",
              border: "1px solid var(--border2)", borderRadius: 7,
              color: "var(--t3)", fontSize: 12, fontWeight: 500, cursor: "pointer",
            }}
          >
            Log Out
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>

        {/* Tab bar */}
        <div style={{ borderBottom: "1px solid var(--border)", padding: "0 28px", display: "flex", gap: 0 }}>
          {["users", "overview", "teams", "settings", "observability", "mailboxes", "credits"].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: "14px 18px", fontSize: 13, fontWeight: 600,
                background: "transparent", border: "none", cursor: "pointer",
                textTransform: "capitalize",
                color: tab === t ? "var(--amber)" : "var(--t3)",
                borderBottom: tab === t ? "2px solid var(--amber)" : "2px solid transparent",
                marginBottom: -1,
              }}
            >{{ users: "Users", overview: "Overview", teams: "Teams", settings: "Settings", observability: "Observability", mailboxes: "Mailboxes", credits: "Credits" }[t]}</button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, padding: "24px 28px", overflowY: "auto" }}>

          {tab === "overview" && (
            <PanelErrorBoundary errorTestId="overview-panel-error">
            {overviewError ? (
              <div data-testid="overview-error" style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
                <ObsErrorState error={overviewError} />
              </div>
            ) : !overviewData ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
                <span style={{ color: "var(--t3)", fontSize: 14 }}>Loading...</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

                {/* Top 4 stat cards */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                  {[
                    { label: "Managers",        value: overviewData.total_managers },
                    { label: "Associates",       value: overviewData.total_associates },
                    { label: "Total Projects",   value: overviewData.total_projects },
                    { label: "Experts Sourced",  value: overviewData.total_experts },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: "var(--bg2)", border: "1px solid var(--bg3)", borderRadius: 8, padding: 20 }}>
                      <div style={{ fontSize: 12, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>{label}</div>
                      <div style={{ fontSize: 32, fontWeight: 700, color: "var(--amber)" }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Pitched / Engaged */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
                  {[
                    { label: "Pitched",  value: overviewData.total_pitched },
                    { label: "Engaged",  value: overviewData.total_engaged },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: "var(--bg2)", border: "1px solid var(--bg3)", borderRadius: 8, padding: 20 }}>
                      <div style={{ fontSize: 12, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>{label}</div>
                      <div style={{ fontSize: 32, fontWeight: 700, color: "var(--amber)" }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Projects by Tier */}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--amber)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 12 }}>Projects by Tier</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {(() => {
                      const max = Math.max(1, ...overviewData.projects_by_tier.map(r => r.count));
                      return overviewData.projects_by_tier.map(r => (
                        <div key={r.tier} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ width: 100, fontSize: 13, color: "var(--t2)", flexShrink: 0 }}>{r.tier ?? "—"}</div>
                          <div style={{ flex: 1, height: 10, background: "var(--bg3)", borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ width: `${(r.count / max) * 100}%`, height: "100%", background: "var(--amber)", opacity: 0.3 }} />
                          </div>
                          <div style={{ width: 32, fontSize: 13, color: "var(--t2)", textAlign: "right" }}>{r.count}</div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Top Associates */}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--amber)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 12 }}>Top Associates</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {overviewData.top_associates.map(r => (
                      <div key={r.username} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 13, color: "var(--t2)" }}>{r.username}</span>
                        <span style={{ fontSize: 13, color: "var(--amber)", fontWeight: 600 }}>{r.project_count}</span>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            )}
            </PanelErrorBoundary>
          )}

          {tab === "users" && (
            <>
              {/* Header row */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "var(--t1)" }}>Users</span>
                <button
                  onClick={() => { setShowCreateForm(v => !v); setEditingUser(null); }}
                  style={{
                    background: "var(--amber-bg)", border: "1px solid var(--amber-bdr)",
                    color: "var(--amber)", fontSize: 12, fontWeight: 600,
                    padding: "7px 16px", borderRadius: 8, cursor: "pointer",
                  }}
                >
                  {showCreateForm ? "Cancel" : "+ Create User"}
                </button>
              </div>

              {/* Create form */}
              {showCreateForm && (
                <form onSubmit={createUser} style={panelStyle}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t1)", marginBottom: 2 }}>New User</div>
                  <div>
                    <label style={labelStyle}>Username</label>
                    <input
                      style={inputStyle}
                      value={newUser.username}
                      onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))}
                      required
                      onFocus={e => { e.currentTarget.style.borderColor = "var(--amber-dim)"; }}
                      onBlur={e => { e.currentTarget.style.borderColor = "var(--border2)"; }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Role</label>
                    <RoleToggle value={newUser.role} onChange={r => setNewUser(u => ({ ...u, role: r }))} />
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      style={{ padding: "7px 14px", background: "transparent", border: "1px solid var(--border2)", borderRadius: 7, color: "var(--t3)", fontSize: 12, cursor: "pointer" }}
                    >Cancel</button>
                    <button
                      type="submit"
                      style={{ padding: "7px 18px", background: "var(--amber-bg)", border: "1px solid var(--amber-bdr)", borderRadius: 7, color: "var(--amber)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                    >Create</button>
                  </div>
                </form>
              )}

              {/* Invite link after create */}
              {createdInvite && (
                <div style={{ ...panelStyle, borderColor: "var(--amber-bdr)" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t1)", marginBottom: 6 }}>
                    Invite link for {createdInvite.username}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--t3)", marginBottom: 8 }}>
                    Expires in 72 hours.
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      readOnly
                      value={inviteLink(createdInvite.token)}
                      style={{ ...inputStyle, fontSize: 11, flex: 1 }}
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(inviteLink(createdInvite.token))}
                      style={{ padding: "7px 14px", background: "var(--amber-bg)", border: "1px solid var(--amber-bdr)", borderRadius: 7, color: "var(--amber)", fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                    >Copy</button>
                  </div>
                  <button
                    onClick={() => setCreatedInvite(null)}
                    style={{ marginTop: 10, padding: "5px 12px", background: "transparent", border: "1px solid var(--border2)", borderRadius: 6, color: "var(--t3)", fontSize: 11, cursor: "pointer" }}
                  >Dismiss</button>
                </div>
              )}

              {/* Edit form */}
              {editingUser && (
                <form onSubmit={saveEdit} style={panelStyle}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t1)", marginBottom: 2 }}>Edit: {editingUser.username}</div>
                  <div>
                    <label style={labelStyle}>Username</label>
                    <input
                      style={inputStyle}
                      value={editForm.username}
                      onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))}
                      required
                      onFocus={e => { e.currentTarget.style.borderColor = "var(--amber-dim)"; }}
                      onBlur={e => { e.currentTarget.style.borderColor = "var(--border2)"; }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Role</label>
                    <RoleToggle value={editForm.role} onChange={r => setEditForm(f => ({ ...f, role: r }))} />
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      onClick={() => setEditingUser(null)}
                      style={{ padding: "7px 14px", background: "transparent", border: "1px solid var(--border2)", borderRadius: 7, color: "var(--t3)", fontSize: 12, cursor: "pointer" }}
                    >Cancel</button>
                    <button
                      type="submit"
                      style={{ padding: "7px 18px", background: "var(--amber-bg)", border: "1px solid var(--amber-bdr)", borderRadius: 7, color: "var(--amber)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                    >Save</button>
                  </div>
                </form>
              )}

              {/* Password link invite panel */}
              {pendingInvite && (
                <div style={{ ...panelStyle, borderColor: "var(--amber-bdr)" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t1)", marginBottom: 6 }}>
                    Password link for {pendingInvite.username}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--t3)", marginBottom: 8 }}>
                    Expires in 72 hours.
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      readOnly
                      value={inviteLink(pendingInvite.token)}
                      style={{ ...inputStyle, fontSize: 11, flex: 1 }}
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(inviteLink(pendingInvite.token))}
                      style={{ padding: "7px 14px", background: "var(--amber-bg)", border: "1px solid var(--amber-bdr)", borderRadius: 7, color: "var(--amber)", fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                    >Copy</button>
                  </div>
                  <button
                    onClick={() => setPendingInvite(null)}
                    style={{ marginTop: 10, padding: "5px 12px", background: "transparent", border: "1px solid var(--border2)", borderRadius: 6, color: "var(--t3)", fontSize: 11, cursor: "pointer" }}
                  >Dismiss</button>
                </div>
              )}

              {/* Users table */}
              <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
                {/* Header */}
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 120px 120px 1fr",
                  padding: "10px 18px", background: "var(--bg3)",
                  borderBottom: "1px solid var(--border)",
                  fontSize: 10, fontWeight: 700, color: "var(--t3)",
                  textTransform: "uppercase", letterSpacing: "0.06em",
                }}>
                  <span>Username</span>
                  <span>Role</span>
                  <span>Credits</span>
                  <span>Actions</span>
                </div>

                {users.length === 0 ? (
                  <div style={{ padding: "32px 18px", textAlign: "center", color: "var(--t3)", fontSize: 13 }}>
                    No users yet.
                  </div>
                ) : (
                  users.map(user => (
                    <div key={user.id}>
                      <div
                        style={{
                          display: "grid", gridTemplateColumns: "1fr 120px 120px 1fr",
                          padding: "13px 18px", borderBottom: grantTarget === user.id ? "none" : "1px solid var(--border)",
                          alignItems: "center",
                          opacity: user.is_active === false || user.is_active === 0 ? 0.6 : 1,
                        }}
                      >
                        <span style={{ fontSize: 13, color: "var(--t1)", fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
                          {user.username}
                          {(user.is_active === false || user.is_active === 0) && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: "var(--t3)", background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 4, padding: "1px 6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Inactive</span>
                          )}
                        </span>
                        <span><RoleChip role={user.role} /></span>
                        <span data-testid="user-credits" style={{ fontSize: 13, color: "var(--t2)", fontVariantNumeric: "tabular-nums" }}>
                          {user.credits != null ? user.credits : '—'}
                        </span>
                        <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button
                            onClick={() => { openEdit(user); setShowCreateForm(false); }}
                            style={{ padding: "4px 10px", background: "transparent", border: "1px solid var(--border2)", borderRadius: 6, color: "var(--t2)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                          >Edit</button>
                          {(user.is_active !== false && user.is_active !== 0) && (
                            <button
                              onClick={() => issuePasswordLink(user)}
                              style={{ padding: "4px 10px", background: "transparent", border: "1px solid var(--border2)", borderRadius: 6, color: "var(--t2)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                            >Password link</button>
                          )}
                          <button
                            data-testid="grant-credits-btn"
                            onClick={() => { setGrantTarget(user.id); setGrantAmount(''); setGrantError(null); }}
                            style={{ padding: "4px 10px", background: "transparent", border: "1px solid var(--border2)", borderRadius: 6, color: "var(--t2)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                          >Grant credits</button>
                          {(user.is_active === false || user.is_active === 0) ? (
                            <button
                              onClick={() => reactivateUser(user)}
                              style={{ padding: "4px 10px", background: "transparent", border: "1px solid rgba(90,180,100,0.35)", borderRadius: 6, color: "#5ab464", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                            >Reactivate</button>
                          ) : (
                            <button
                              onClick={() => deactivateUser(user)}
                              style={{ padding: "4px 10px", background: "transparent", border: "1px solid rgba(224,80,80,0.25)", borderRadius: 6, color: "var(--red)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                            >Deactivate</button>
                          )}
                        </span>
                      </div>
                      {grantTarget === user.id && (
                        <div data-testid="grant-credits-form" style={{ padding: "10px 18px 14px 18px", borderBottom: "1px solid var(--border)", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <input
                            type="number"
                            placeholder="credits (negative to adjust)"
                            value={grantAmount}
                            onChange={e => setGrantAmount(e.target.value)}
                            style={{ width: 90, padding: "4px 8px", background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 6, color: "var(--t1)", fontSize: 13 }}
                          />
                          <button
                            onClick={() => handleGrantCredits(user.id)}
                            style={{ padding: "4px 12px", background: "var(--accent)", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                          >Grant</button>
                          <button
                            onClick={() => { setGrantTarget(null); setGrantError(null); }}
                            style={{ padding: "4px 10px", background: "transparent", border: "1px solid var(--border2)", borderRadius: 6, color: "var(--t3)", fontSize: 12, cursor: "pointer" }}
                          >Cancel</button>
                          {grantError && <span style={{ fontSize: 12, color: "var(--red)" }}>{grantError}</span>}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {tab === "teams" && (() => {
            const TIER_ORDER = ['high_priority', 'active', 'long_term', 'on_hold'];
            const TIER_COLOR = { high_priority: '#e07050', active: '#c49a45', long_term: '#5ab4cc', on_hold: '#4d7a90' };
            return (
              <PanelErrorBoundary errorTestId="teams-panel-error">
              <div>
                <div style={{ color: "var(--amber)", fontSize: 20, fontWeight: 600, marginBottom: 20 }}>Teams</div>
                {teamsError ? (
                  <div data-testid="teams-error" style={{ textAlign: "center", color: "var(--t3)" }}>
                    <ObsErrorState error={teamsError} />
                  </div>
                ) : teamsData.length === 0 ? (
                  <div style={{ textAlign: "center", color: "var(--t3)" }}>No managers found.</div>
                ) : teamsData.map(m => (
                  <div key={m.manager_id} style={{ marginBottom: expandedManagers[m.manager_id] ? 0 : 10 }}>
                    <button
                      onClick={() => toggleManager(m.manager_id)}
                      style={{
                        width: "100%", padding: "14px 20px",
                        background: "rgba(8,18,30,0.6)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        borderRadius: expandedManagers[m.manager_id] ? "10px 10px 0 0" : 10,
                        cursor: "pointer", display: "flex", alignItems: "center",
                        marginBottom: expandedManagers[m.manager_id] ? 0 : 10,
                      }}
                    >
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--amber)", marginRight: 10, flexShrink: 0 }} />
                      <span style={{ color: "var(--t2)", fontSize: 14, fontWeight: 600, flex: 1, textAlign: "left" }}>{m.manager_username}</span>
                      <span style={{ color: "var(--t3)", fontSize: 11, marginRight: 16 }}>{m.associates.length} Associates</span>
                      <span style={{ color: "var(--t3)", fontSize: 11 }}>{expandedManagers[m.manager_id] ? "▼" : "▶"}</span>
                    </button>

                    {expandedManagers[m.manager_id] && (
                      <div style={{ border: "1px solid rgba(255,255,255,0.06)", borderTop: "none", borderRadius: "0 0 10px 10px", padding: "10px 14px", background: "rgba(8,18,30,0.3)" }}>
                        {m.associates.map(a => (
                          <div key={a.associate_id} style={{ marginBottom: 4 }}>
                            <button
                              onClick={() => toggleAssociate(a.associate_id)}
                              style={{
                                width: "100%", padding: "12px 16px",
                                background: "rgba(255,255,255,0.02)",
                                border: "none", borderLeft: "3px solid #22c55e",
                                borderRadius: "0 6px 6px 0",
                                marginBottom: 4, display: "flex", alignItems: "center",
                                cursor: "pointer",
                              }}
                            >
                              <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", marginRight: 10, flexShrink: 0 }} />
                              <span style={{ color: "var(--t2)", fontSize: 13, fontWeight: 600, flex: 1, textAlign: "left" }}>{a.associate_username}</span>
                              <span style={{ color: "var(--t3)", fontSize: 11, marginRight: 12 }}>{a.assignments.length} Projects</span>
                              <span style={{ color: "var(--t3)", fontSize: 11 }}>{expandedAssociates[a.associate_id] ? "▼" : "▶"}</span>
                            </button>

                            {expandedAssociates[a.associate_id] && (
                              <div style={{ paddingLeft: 16, marginBottom: 4 }}>
                                {[...a.assignments]
                                  .sort((x, y) => TIER_ORDER.indexOf(x.priority_tier) - TIER_ORDER.indexOf(y.priority_tier))
                                  .map((asgn, idx) => {
                                    const tierColor = TIER_COLOR[asgn.priority_tier] || "var(--t3)";
                                    return (
                                      <div key={asgn.assignment_id} style={{
                                        borderLeft: `3px solid ${tierColor}`,
                                        padding: "10px 14px", marginBottom: 4,
                                        background: "rgba(255,255,255,0.01)",
                                        borderRadius: "0 6px 6px 0",
                                      }}>
                                        <div style={{ color: "var(--amber)", fontSize: 13, fontWeight: 700 }}>
                                          {idx + 1}. {asgn.ticker}
                                        </div>
                                        {asgn.context && (
                                          <div style={{ color: "var(--t3)", fontSize: 11, marginTop: 4 }}>{asgn.context}</div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              </PanelErrorBoundary>
            );
          })()}

          {tab === "settings" && (() => {
            const inputStyle = {
              width: "100%", background: "var(--bg3)", border: "1px solid var(--border2)",
              borderRadius: 6, color: "var(--t2)", fontSize: 13,
              padding: "8px 12px", outline: "none", boxSizing: "border-box",
            };
            const labelStyle = { display: "block", fontSize: 12, color: "var(--t3)", marginBottom: 6 };
            const sectionLabelStyle = {
              fontSize: 11, fontWeight: 700, color: "var(--t3)",
              textTransform: "uppercase", letterSpacing: "0.08em",
              marginBottom: 8, marginTop: 20,
            };
            const field = (label, key, type = "text") => (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>{label}</label>
                <input
                  type={type}
                  style={inputStyle}
                  value={globalSettings[key] || ""}
                  onChange={e => setGlobalSettings(prev => ({ ...prev, [key]: e.target.value }))}
                />
              </div>
            );
            return (
              <div>
                <div style={{ color: "var(--amber)", fontSize: 20, fontWeight: 600, marginBottom: 24 }}>Global Settings</div>
                <div style={{ maxWidth: 600 }}>
                  <div style={sectionLabelStyle}>PhantomBuster</div>
                  {field("Inbox Scraper Phantom ID", "inbox_scraper_phantom_id")}
                  {field("PhantomBuster API Key", "phantombuster_api_key", "password")}

                  <div style={sectionLabelStyle}>Instantly</div>
                  {field("Instantly API Key", "instantly_api_key", "password")}
                  {field("Default Campaign ID", "instantly_default_campaign_id")}

                  <div style={sectionLabelStyle}>Safety Rails</div>
                  {field("Follow-up Delay (days)", "follow_up_delay_days", "number")}
                  {field("LinkedIn Batch Delay (minutes)", "linkedin_batch_delay_minutes", "number")}

                  <div style={{ display: "flex", alignItems: "center", marginTop: 24 }}>
                    <button
                      onClick={saveGlobalSettings}
                      style={{
                        background: "var(--amber)", color: "#000", border: "none",
                        borderRadius: 8, fontSize: 13, fontWeight: 600,
                        padding: "10px 24px", cursor: "pointer",
                      }}
                    >Save Settings</button>
                    {settingsSaved && <span style={{ color: "#22c55e", fontSize: 13, marginLeft: 12 }}>Saved!</span>}
                  </div>
                </div>
              </div>
            );
          })()}

          {tab === "observability" && (
            <div>

              {/* ── HEALTH (default expanded) ── */}
              <div style={obsSectionWrap}>
                <div style={obsSectionHead} onClick={() => toggleObs('health')}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "var(--t1)" }}>Health</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {!obsCollapsed.health && (
                      <button
                        onClick={e => { e.stopPropagation(); loadObsHealth(); }}
                        style={{
                          padding: "4px 10px", background: "transparent",
                          border: "1px solid var(--border2)", borderRadius: 6,
                          color: "var(--t2)", fontSize: 11, cursor: "pointer",
                        }}
                      >Refresh</button>
                    )}
                    <ObsChevron collapsed={obsCollapsed.health} />
                  </div>
                </div>

                {!obsCollapsed.health && (
                  <div style={{ borderTop: "1px solid var(--border)" }}>
                    {obsHealthLoading && <div style={obsMuted}>Loading…</div>}
                    {obsHealthError && <ObsErrorState error={obsHealthError} onRetry={loadObsHealth} />}
                    {obsHealth && !obsHealthLoading && (
                      <div>
                        {/* Each section extracted as a child component so PanelErrorBoundary can catch render crashes. */}
                        <PanelErrorBoundary errorTestId="ss-panel-error">
                          <SilentlyStuckSection data={obsHealth.silently_stuck} />
                        </PanelErrorBoundary>
                        <PanelErrorBoundary errorTestId="sr-panel-error">
                          <StuckRunningSection data={obsHealth.stuck_running} />
                        </PanelErrorBoundary>
                        {/* rescue_events_summary — replaces rescued_batches (ADMIN-1a) */}
                        <PanelErrorBoundary errorTestId="rescue-render-error">
                          <RescueEventsSection summary={obsHealth.rescue_events_summary} />
                        </PanelErrorBoundary>
                        <PanelErrorBoundary errorTestId="backlog-panel-error">
                          <UnscorableBacklogSection data={obsHealth.unscorable_backlog} />
                        </PanelErrorBoundary>
                        <PanelErrorBoundary errorTestId="score-errors-panel-error">
                          <ScoreErrorsSection data={obsHealth.score_errors_by_batch} />
                        </PanelErrorBoundary>
                        <PanelErrorBoundary errorTestId="consistency-panel-error">
                          <ConsistencyLogSection data={obsHealth.consistency_log} />
                        </PanelErrorBoundary>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── BATCH INSPECTOR (default collapsed) ── */}
              <div style={obsSectionWrap}>
                <div style={obsSectionHead} onClick={() => toggleObs('inspector')}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "var(--t1)" }}>Batch Inspector</span>
                  <ObsChevron collapsed={obsCollapsed.inspector} />
                </div>

                {!obsCollapsed.inspector && (
                  <div style={{ borderTop: "1px solid var(--border)" }}>
                    <div style={{ padding: "12px 16px", display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="number"
                        placeholder="Batch ID"
                        value={obsBatchId}
                        onChange={e => setObsBatchId(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && loadObsBatch()}
                        style={{ ...inputStyle, width: 120, padding: "6px 10px" }}
                      />
                      <button
                        onClick={loadObsBatch}
                        disabled={obsBatchLoading || !obsBatchId}
                        style={{
                          padding: "6px 14px", background: "var(--amber-bg)",
                          border: "1px solid var(--amber-bdr)", borderRadius: 7,
                          color: "var(--amber)", fontSize: 12, fontWeight: 600,
                          cursor: obsBatchLoading || !obsBatchId ? "default" : "pointer",
                          opacity: obsBatchLoading || !obsBatchId ? 0.5 : 1,
                        }}
                      >{obsBatchLoading ? "Loading…" : "Load"}</button>
                    </div>

                    {obsBatchError && <ObsErrorState error={obsBatchError} onRetry={loadObsBatch} />}

                    {obsBatchData && (
                      <PanelErrorBoundary errorTestId="batch-inspector-panel-error">
                        <BatchInspectorContent batchData={obsBatchData} />
                      </PanelErrorBoundary>
                    )}
                  </div>
                )}
              </div>

              {/* ── LEAD AUDIT (default collapsed) ── */}
              <div style={obsSectionWrap}>
                <div style={obsSectionHead} onClick={() => toggleObs('audit')}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "var(--t1)" }}>Lead Audit</span>
                  <ObsChevron collapsed={obsCollapsed.audit} />
                </div>

                {!obsCollapsed.audit && (
                  <div style={{ borderTop: "1px solid var(--border)" }}>
                    <div style={{ padding: "12px 16px", display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="number"
                        placeholder="Lead ID"
                        value={obsLeadId}
                        onChange={e => setObsLeadId(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && loadObsLead()}
                        style={{ ...inputStyle, width: 120, padding: "6px 10px" }}
                      />
                      <button
                        onClick={loadObsLead}
                        disabled={obsLeadLoading || !obsLeadId}
                        style={{
                          padding: "6px 14px", background: "var(--amber-bg)",
                          border: "1px solid var(--amber-bdr)", borderRadius: 7,
                          color: "var(--amber)", fontSize: 12, fontWeight: 600,
                          cursor: obsLeadLoading || !obsLeadId ? "default" : "pointer",
                          opacity: obsLeadLoading || !obsLeadId ? 0.5 : 1,
                        }}
                      >{obsLeadLoading ? "Loading…" : "Load"}</button>
                    </div>

                    {obsLeadError && <ObsErrorState error={obsLeadError} onRetry={loadObsLead} />}

                    {obsLeadData && (
                      <PanelErrorBoundary errorTestId="lead-audit-panel-error">
                        <LeadAuditContent leadData={obsLeadData} />
                      </PanelErrorBoundary>
                    )}
                  </div>
                )}
              </div>

            </div>
          )}

          {tab === "mailboxes" && (() => {
            const associates = users.filter(u => u.role === 'associate' && u.is_active);
            return (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--t1)" }}>Mailbox Ownership</h3>
                  <button onClick={fetchMailboxes} style={{ padding: "5px 12px", background: "transparent", border: "1px solid var(--border2)", borderRadius: 6, color: "var(--t2)", fontSize: 12, cursor: "pointer" }}>Refresh</button>
                </div>
                {mailboxesError && <div style={{ color: "var(--red)", marginBottom: 12, fontSize: 13 }}>{mailboxesError}</div>}
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--border)" }}>
                      <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--t2)", fontWeight: 600 }}>Email</th>
                      <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--t2)", fontWeight: 600 }}>Owner</th>
                      <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--t2)", fontWeight: 600 }}>Status</th>
                      <th style={{ padding: "6px 10px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {mailboxes.map(mb => (
                      <tr key={mb.email} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "8px 10px", color: "var(--t1)" }}>{mb.email}</td>
                        <td style={{ padding: "8px 10px" }}>
                          <select
                            value={mb.owner ? mb.owner.user_id : ''}
                            onChange={e => assignMailbox(mb.email, e.target.value === '' ? null : parseInt(e.target.value, 10))}
                            style={{ padding: "3px 6px", borderRadius: 4, border: "1px solid var(--border2)", background: "var(--bg2)", color: "var(--t1)", fontSize: 13 }}
                          >
                            <option value="">Unassigned</option>
                            {associates.map(u => (
                              <option key={u.id} value={u.id}>{u.username}</option>
                            ))}
                          </select>
                          {mailboxesInlineErrors[mb.email] && (
                            <span style={{ color: "var(--red)", fontSize: 11, marginLeft: 6 }}>{mailboxesInlineErrors[mb.email]}</span>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px", color: mb.in_instantly ? "var(--green, #3a7)" : "var(--t3)" }}>
                          {mb.in_instantly ? "In Instantly" : "Not in Instantly"}
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          {!mb.in_instantly && (
                            <button
                              onClick={() => assignMailbox(mb.email, null)}
                              style={{ padding: "3px 8px", background: "transparent", border: "1px solid var(--border2)", borderRadius: 4, color: "var(--t2)", fontSize: 12, cursor: "pointer" }}
                            >Remove</button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {mailboxes.length === 0 && !mailboxesError && (
                      <tr><td colSpan={4} style={{ padding: "16px 10px", color: "var(--t3)", textAlign: "center" }}>No mailboxes found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {tab === "credits" && (
            <div>
              <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700, color: "var(--t1)" }}>Credit Ledger by User</h3>
              {adminCreditsError && <div style={{ color: "var(--red)", fontSize: 13, marginBottom: 12 }}>Failed to load credits.</div>}
              {!adminCredits && !adminCreditsError && <div style={{ color: "var(--t3)", fontSize: 13 }}>Loading...</div>}
              {adminCredits && adminCredits.users.map(u => (
                <div key={u.user_id} style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t1)", marginBottom: 6 }}>
                    {u.username} <span style={{ fontWeight: 400, color: "var(--t3)" }}>balance: {u.balance.toLocaleString()}</span>
                  </div>
                  {u.rows.length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--t3)" }}>No ledger rows.</div>
                  ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid var(--border)" }}>
                          <th style={{ textAlign: "left", padding: "4px 8px", color: "var(--t3)", fontWeight: 600 }}>Kind</th>
                          <th style={{ textAlign: "right", padding: "4px 8px", color: "var(--t3)", fontWeight: 600 }}>Credits</th>
                          <th style={{ textAlign: "left", padding: "4px 8px", color: "var(--t3)", fontWeight: 600 }}>Note</th>
                          <th style={{ textAlign: "left", padding: "4px 8px", color: "var(--t3)", fontWeight: 600 }}>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {u.rows.map(row => (
                          <tr key={row.id} style={{ borderBottom: "1px solid var(--border)" }}>
                            <td style={{ padding: "4px 8px", color: "var(--t2)" }}>{row.kind}</td>
                            <td style={{ padding: "4px 8px", textAlign: "right", color: row.credits >= 0 ? "var(--green, #3a7)" : "var(--red)" }}>{row.credits > 0 ? '+' : ''}{row.credits}</td>
                            <td style={{ padding: "4px 8px", color: "var(--t3)" }}>{row.note || '—'}</td>
                            <td style={{ padding: "4px 8px", color: "var(--t3)" }}>{row.created_at ? row.created_at.slice(0, 10) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
